import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import "./NotificationBell.css";

/*
 * NotificationBell — sidebar widget that surfaces tags whose end-of-life
 * is within the warning window (default 30 days) or already past it.
 *
 *   - Polls /tags/expiring every POLL_MS so the count stays fresh without
 *     the user having to refresh.
 *   - A coloured rim + badge count flags "something to see": amber when any
 *     tag is approaching, red when anything is already overdue. The badge
 *     counts only UNSEEN items — opening the panel marks everything shown
 *     as seen, so the pulse stops until a new expiring tag appears.
 *   - Seen-state persists in localStorage so it survives refresh.
 *   - The dropdown lists each tag with its product name, current room,
 *     and days remaining. New-since-last-open tags get a "new" dot so you
 *     can still tell what just appeared. Clicking a row navigates to that
 *     tag's details page.
 *   - Click-outside / Escape close the panel for keyboard parity.
 */

const POLL_MS = 60 * 1000;     // Refresh every 60s.
const WITHIN_DAYS = 30;        // What counts as "nearing end".
const SEEN_STORAGE_KEY = "rfid_notif_seen_tids";

// Persist/retrieve the acknowledged-tids set. Kept as an array on disk so
// localStorage doesn't have to JSON-round-trip a Set.
const loadSeen = () => {
  try {
    const raw = localStorage.getItem(SEEN_STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
};
const saveSeen = (set) => {
  try { localStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify([...set])); } catch { /* quota */ }
};

const formatRelative = (daysRemaining) => {
  if (daysRemaining == null) return "";
  if (daysRemaining < 0)  return `${Math.abs(daysRemaining)}d overdue`;
  if (daysRemaining === 0) return "Today";
  if (daysRemaining === 1) return "Tomorrow";
  return `${daysRemaining}d left`;
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [expiredCount, setExpiredCount] = useState(0);
  const [nearingCount, setNearingCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // Set of TIDs the user has already acknowledged. Persisted.
  const [seenTids, setSeenTids] = useState(() => loadSeen());
  const panelRef = useRef(null);
  const buttonRef = useRef(null);

  const fetchExpiring = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getExpiringTags(WITHIN_DAYS);
      const nextItems = res.items || [];
      setItems(nextItems);
      setExpiredCount(res.expiredCount || 0);
      setNearingCount(res.nearingCount || 0);

      // Prune: forget tids that are no longer on the list (e.g. the end
      // date was cleared or extended past the window). Keeps the set
      // small and means a tag re-entering the window is "new" again.
      const currentTids = new Set(nextItems.map((i) => i.tid).filter(Boolean));
      setSeenTids((prev) => {
        const trimmed = new Set([...prev].filter((tid) => currentTids.has(tid)));
        if (trimmed.size !== prev.size) saveSeen(trimmed);
        return trimmed;
      });
    } catch {
      /* swallow — sidebar shouldn't toast on every poll failure */
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial + polling fetch.
  useEffect(() => {
    fetchExpiring();
    const id = setInterval(fetchExpiring, POLL_MS);
    return () => clearInterval(id);
  }, [fetchExpiring]);

  // Opening the panel acknowledges every currently-listed tid so the
  // badge count drops to zero until a new expiring tag shows up.
  useEffect(() => {
    if (!open || items.length === 0) return;
    setSeenTids((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const it of items) {
        if (it.tid && !next.has(it.tid)) {
          next.add(it.tid);
          changed = true;
        }
      }
      if (changed) saveSeen(next);
      return changed ? next : prev;
    });
  }, [open, items]);

  // Close on click-outside / Escape.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (buttonRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Derive unread counts from the live items + the seen set. The bell's
  // tone + badge reflect only UNREAD items — once the user opens the panel
  // everything becomes "seen" and the red/amber pulse calms until a new
  // tag drops into the window.
  const unreadItems = items.filter((i) => !i.tid || !seenTids.has(i.tid));
  const unreadExpired = unreadItems.filter((i) => i.expired).length;
  const unreadNearing = unreadItems.length - unreadExpired;
  const unreadTotal = unreadItems.length;
  const totalInWindow = items.length;
  const tone = unreadExpired > 0 ? "danger" : unreadNearing > 0 ? "warn" : "calm";

  return (
    <div className="notif-bell-root">
      <button
        ref={buttonRef}
        type="button"
        className={`notif-bell-btn notif-tone-${tone}`}
        onClick={() => { setOpen((o) => !o); if (!open) fetchExpiring(); }}
        title={unreadTotal === 0
          ? (totalInWindow === 0
              ? "No expiring items"
              : `${totalInWindow} expiring item${totalInWindow === 1 ? "" : "s"} (all reviewed)`)
          : `${unreadTotal} new item${unreadTotal === 1 ? "" : "s"} need attention`}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <i className="fa fa-bell" aria-hidden />
        {unreadTotal > 0 && (
          <span className={`notif-bell-badge notif-badge-${tone}`}>
            {unreadTotal > 99 ? "99+" : unreadTotal}
          </span>
        )}
      </button>

      {open && (
        <div ref={panelRef} className="notif-panel">
          <div className="notif-panel-header">
            <div>
              <h4 className="notif-panel-title">Expiring items</h4>
              <p className="notif-panel-subtitle">
                Tags whose end date is within {WITHIN_DAYS} days.
              </p>
            </div>
            <button
              type="button"
              className="notif-refresh-btn"
              onClick={fetchExpiring}
              disabled={loading}
              title="Refresh"
            >
              <i className={`fa ${loading ? "fa-spinner fa-spin" : "fa-refresh"}`} aria-hidden />
            </button>
          </div>

          <div className="notif-panel-summary">
            <span className={`notif-summary-chip notif-summary-danger${expiredCount ? "" : " is-empty"}`}>
              <i className="fa fa-exclamation-circle" aria-hidden />
              {expiredCount} overdue
            </span>
            <span className={`notif-summary-chip notif-summary-warn${nearingCount ? "" : " is-empty"}`}>
              <i className="fa fa-clock-o" aria-hidden />
              {nearingCount} soon
            </span>
          </div>

          <div className="notif-panel-body">
            {totalInWindow === 0 ? (
              <div className="notif-empty">
                <i className="fa fa-check-circle" aria-hidden />
                <p>Nothing expiring. You're good.</p>
              </div>
            ) : (
              items.map((item) => {
                const tone = item.expired ? "danger" : "warn";
                // An item is "new" only on the first open after it arrived.
                // Once the open-effect marks it seen, subsequent renders
                // drop the dot.
                const isNew = item.tid && !seenTids.has(item.tid);
                return (
                  <button
                    key={item._id || item.tid}
                    type="button"
                    className={`notif-item notif-item-${tone}${isNew ? " is-new" : ""}`}
                    onClick={() => {
                      setOpen(false);
                      // Each bell item is a physical tag, so deep-link to
                      // its details page. Falls back to the inventory
                      // overview if the backend didn't return a tid.
                      if (item.tid) navigate(`/tags/${encodeURIComponent(item.tid)}`);
                      else navigate("/product-list");
                    }}
                    title="Open tag details"
                  >
                    <div className={`notif-item-accent notif-accent-${tone}`} />
                    <div className="notif-item-main">
                      <div className="notif-item-top">
                        <span className="notif-item-name">
                          {isNew && <span className={`notif-new-dot notif-new-${tone}`} aria-label="New" />}
                          {item.name || "Unregistered product"}
                        </span>
                        <span className={`notif-item-when notif-when-${tone}`}>
                          {formatRelative(item.daysRemaining)}
                        </span>
                      </div>
                      <div className="notif-item-bottom">
                        <span className="notif-item-tid"><i className="fa fa-tag" aria-hidden /> {item.tid || "—"}</span>
                        {item.roomTitle && (
                          <span className="notif-item-room">
                            <i className="fa fa-cube" aria-hidden /> {item.roomTitle}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
